import axios from 'axios';
import { useAuthStore } from '../store/authSlice';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:2080/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only auto-logout on 401 for authenticated requests (not login attempts)
    // Check if this is NOT a login/auth request
    const isAuthRequest = error.config?.url?.includes('/auth/login') || 
                          error.config?.url?.includes('/auth/telegram');
    
    if (error.response?.status === 401 && !isAuthRequest) {
      // Token expired or invalid - only if we had a token
      const hasToken = useAuthStore.getState().token;
      if (hasToken) {
        console.log('🔒 401 on authenticated request - logging out');
        useAuthStore.getState().logout();
        // Don't force reload - let React Router handle navigation
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const authApi = {
  login: (login: string, password: string) => 
    api.post('/auth/login', { login, password }),
  
  telegramAuth: (initData: string) => 
    api.post('/auth/telegram', { init_data: initData }),
  
  refresh: () => 
    api.post('/auth/refresh'),
  
  me: () => 
    api.get('/auth/me'),
  
  logout: () => 
    api.post('/auth/logout'),
};

export const usersApi = {
  getMe: () => api.get('/users/me'),
  getMeWithGroups: () => api.get('/users/me/with-groups'),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: { login: string; password?: string; first_name?: string; last_name?: string; email?: string; telegram_id?: number; telegram_username?: string }) => 
    api.post('/users', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/users/${id}`, data),
  updateStatus: (id: string, status: string) => api.put(`/users/${id}/status`, { status }),
  changePassword: (id: string, currentPassword: string | null, newPassword: string) => 
    api.put(`/users/${id}/password`, { current_password: currentPassword || '', new_password: newPassword }),
  delete: (id: string) => api.delete(`/users/${id}`),
  hardDelete: (id: string) => api.delete(`/users/${id}/permanent`),
  restore: (id: string) => api.post(`/users/${id}/restore`),
  list: (params?: { limit?: number; offset?: number; search?: string }) => 
    api.get('/users', { params }),
  listDeleted: (params?: { limit?: number; offset?: number }) => 
    api.get('/users/deleted', { params }),
  getGroups: (id: string) => api.get(`/users/${id}/groups`),
  getPermissions: (id: string) => api.get(`/users/${id}/permissions`),
};

export const groupsApi = {
  list: () => api.get('/groups'),
  getById: (id: string) => api.get(`/groups/${id}`),
  create: (data: { name: string; description?: string }) => api.post('/groups', data),
  update: (id: string, data: { name: string; description?: string }) => api.put(`/groups/${id}`, data),
  delete: (id: string) => api.delete(`/groups/${id}`),
  getPermissions: (id: string) => api.get(`/groups/${id}/permissions`),
  setPermissions: (id: string, permissions: string[]) => api.put(`/groups/${id}/permissions`, { permissions }),
  addPermission: (id: string, permission: string) => api.post(`/groups/${id}/permissions`, { permission }),
  removePermission: (id: string, permission: string) => api.delete(`/groups/${id}/permissions/${permission}`),
  getUsers: (id: string) => api.get(`/groups/${id}/users`),
  addUser: (groupId: string, userId: string) => api.post(`/groups/${groupId}/users`, { user_id: userId }),
  removeUser: (groupId: string, userId: string) => api.delete(`/groups/${groupId}/users/${userId}`),
};

export const permissionsApi = {
  list: () => api.get('/permissions'),
};

export const telegramApi = {
  getUser: (telegramId: number) => api.get(`/telegram/user/${telegramId}`),
};

export const schedulerApi = {
  listTasks: () => api.get('/scheduler/tasks'),
  triggerTask: (taskId: string) => api.post(`/scheduler/trigger/${taskId}`),
};

export const searchApi = {
  search: (query: string, params?: { types?: string; limit?: number; offset?: number }) =>
    api.get('/search', { params: { q: query, ...params } }),
  searchUsers: (query: string, params?: { limit?: number; offset?: number }) =>
    api.get('/search/users', { params: { q: query, ...params } }),
  searchGroups: (query: string, params?: { limit?: number; offset?: number }) =>
    api.get('/search/groups', { params: { q: query, ...params } }),
};

// Profile Types API
export const profileTypesApi = {
  // Get all registered profile types
  list: () => api.get('/profile-types'),
  
  // Get users with specific profile type
  getUsersByType: (profileType: string, params?: { limit?: number; offset?: number }) =>
    api.get(`/profiles/${profileType}/users`, { params }),
};

// Group Profile Types API
export const groupProfilesApi = {
  // Get profile types linked to a group
  getByGroup: (groupId: string) => api.get(`/groups/${groupId}/profile-types`),
  
  // Link group to profile type
  link: (groupId: string, profileType: string, attributeOverrides?: Record<string, unknown>) =>
    api.post(`/groups/${groupId}/profile-types`, { profile_type: profileType, attribute_overrides: attributeOverrides }),
  
  // Update group profile overrides
  updateOverrides: (groupId: string, profileType: string, attributeOverrides: Record<string, unknown>) =>
    api.put(`/groups/${groupId}/profile-types/${profileType}`, { attribute_overrides: attributeOverrides }),
  
  // Unlink group from profile type
  unlink: (groupId: string, profileType: string) =>
    api.delete(`/groups/${groupId}/profile-types/${profileType}`),
};

// User Profiles API
export const userProfilesApi = {
  // Get user's effective profiles with sources
  getByUser: (userId: string) => api.get(`/users/${userId}/profiles`),
  
  // Create or update user profile
  set: (userId: string, profileType: string, attributes?: Record<string, unknown>) =>
    api.post(`/users/${userId}/profiles`, { profile_type: profileType, attributes }),
  
  // Update user profile attributes
  update: (userId: string, profileType: string, attributes: Record<string, unknown>) =>
    api.put(`/users/${userId}/profiles/${profileType}`, { attributes }),
  
  // Delete user profile
  delete: (userId: string, profileType: string) =>
    api.delete(`/users/${userId}/profiles/${profileType}`),
};
